import { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Copy, Trash2, FileText, MoreVertical } from 'lucide-react';
import type { Slide } from '../../../utils/supabase/client';

// ─── Types ────────────────────────────────────────────────────────
interface SlideTypeOption {
  value: string;
  label: string;
  icon: any;
  category: string;
}

interface DraggableSlideListProps {
  slides: Slide[];
  selectedSlide: number;
  slideTypes: SlideTypeOption[];
  getSlideIcon: (type: string) => any;
  onSelectSlide: (index: number) => void;
  onDuplicateSlide: (index: number) => void;
  onDeleteSlide: (index: number) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
}

// ─── Sortable Slide Item ──────────────────────────────────────────
function SortableSlideItem({
  slide,
  index,
  isSelected,
  slideTypes,
  getSlideIcon,
  onSelect,
  onDuplicate,
  onDelete,
}: {
  slide: Slide;
  index: number;
  isSelected: boolean;
  slideTypes: SlideTypeOption[];
  getSlideIcon: (type: string) => any;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto' as any,
  };

  const Icon = getSlideIcon(slide.type);
  const slideTitle = slide.content?.title || 'Untitled';
  const slideType = slideTypes.find(t => t.value === slide.type)?.label || 'Slide';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative rounded-xl transition-all duration-200 ${
        isDragging
          ? 'shadow-lg ring-2 ring-primary/40 bg-card'
          : isSelected
          ? 'shadow-md bg-card ring-2 ring-primary'
          : 'bg-card border border-border hover:border-primary/30 hover:shadow-sm'
      }`}
    >
      {/* Left accent bar for selected slide */}
      {isSelected && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-xl" />
      )}

      {/* Row 1: Drag handle + slide info */}
      <div className="flex items-center w-full pl-1 pr-2 py-2.5">
        {/* Drag Handle */}
        <button
          className={`flex-shrink-0 w-6 self-stretch flex items-center justify-center cursor-grab active:cursor-grabbing transition-colors touch-none ${
            isSelected
              ? 'text-primary/60 hover:text-primary'
              : 'text-muted-foreground/30 hover:text-muted-foreground/70 group-hover:text-muted-foreground/50'
          }`}
          aria-label={`Drag to reorder slide ${index + 1}`}
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Slide Selection Area */}
        <button
          onClick={onSelect}
          className="flex-1 min-w-0 flex items-center gap-2 text-left cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary rounded-lg px-1 py-0.5"
          aria-label={`Select slide ${index + 1}: ${slideTitle} (${slideType})`}
          aria-pressed={isSelected}
          aria-current={isSelected ? 'page' : undefined}
        >
          <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
            isSelected
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground'
          }`}>
            {index + 1}
          </div>
          <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${
            isSelected
              ? 'bg-primary/10 text-primary'
              : 'bg-muted/60 text-muted-foreground'
          }`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className={`text-sm font-semibold line-clamp-2 ${
              isSelected ? 'text-foreground' : 'text-foreground/80'
            }`} title={slideTitle}>
              {slideTitle}
            </p>
            <p className="text-[11px] text-muted-foreground truncate">
              {slideType}
            </p>
          </div>
        </button>
      </div>

      {/* Row 2: Action buttons — always visible, full width */}
      <div className="flex items-center gap-2 px-3 pb-2.5">
        <button
          onClick={onDuplicate}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-lg bg-muted text-muted-foreground text-xs font-medium hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          type="button"
          aria-label={`Duplicate slide ${index + 1}`}
          title="Duplicate slide"
        >
          <Copy className="w-3.5 h-3.5" />
          Duplicate
        </button>
        <button
          onClick={() => {
            if (confirm('Delete this slide?')) {
              onDelete();
            }
          }}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-lg bg-destructive/10 text-destructive text-xs font-medium hover:bg-destructive/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
          type="button"
          aria-label={`Delete slide ${index + 1}`}
          title="Delete slide"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── Drag Overlay Content ─────────────────────────────────────────
function DragOverlaySlide({
  slide,
  index,
  slideTypes,
  getSlideIcon,
}: {
  slide: Slide;
  index: number;
  slideTypes: SlideTypeOption[];
  getSlideIcon: (type: string) => any;
}) {
  const Icon = getSlideIcon(slide.type);
  const slideTitle = slide.content?.title || 'Untitled';
  const slideType = slideTypes.find(t => t.value === slide.type)?.label || 'Slide';

  return (
    <div className="rounded-xl bg-card ring-1 ring-primary shadow-2xl w-72 overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-xl" />
      <div className="flex items-center gap-0 w-full min-w-0">
        <div className="flex-shrink-0 w-7 self-stretch flex items-center justify-center cursor-grabbing text-primary/60">
          <GripVertical className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-2.5 pl-0.5 pr-2 py-3">
          <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
            {index + 1}
          </div>
          <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground line-clamp-2">{slideTitle}</p>
            <p className="text-[11px] text-muted-foreground truncate">{slideType}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────
export default function DraggableSlideList({
  slides,
  selectedSlide,
  slideTypes,
  getSlideIcon,
  onSelectSlide,
  onDuplicateSlide,
  onDeleteSlide,
  onReorder,
}: DraggableSlideListProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px threshold to differentiate click from drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activeSlide = activeId
    ? slides.find(s => s.id === activeId)
    : null;
  const activeIndex = activeId
    ? slides.findIndex(s => s.id === activeId)
    : -1;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active.id !== over.id) {
        const oldIndex = slides.findIndex(s => s.id === active.id);
        const newIndex = slides.findIndex(s => s.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onReorder(oldIndex, newIndex);
        }
      }
    },
    [slides, onReorder]
  );

  const slideIds = slides.map(s => s.id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={slideIds} strategy={verticalListSortingStrategy}>
        <div className="p-3 space-y-2">
          {slides.map((slide, index) => (
            <SortableSlideItem
              key={slide.id}
              slide={slide}
              index={index}
              isSelected={selectedSlide === index}
              slideTypes={slideTypes}
              getSlideIcon={getSlideIcon}
              onSelect={() => onSelectSlide(index)}
              onDuplicate={() => onDuplicateSlide(index)}
              onDelete={() => onDeleteSlide(index)}
            />
          ))}
        </div>
      </SortableContext>

      {/* Floating drag overlay for smooth animation */}
      <DragOverlay dropAnimation={null}>
        {activeSlide ? (
          <DragOverlaySlide
            slide={activeSlide}
            index={activeIndex}
            slideTypes={slideTypes}
            getSlideIcon={getSlideIcon}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
